import { useCallback, useEffect, useRef, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Checklist « points à aborder » d'une salle (tuteur seul — migration 0247).
 * Lit les séries DU tuteur (`visio_instruction_series`, RLS propriétaire) et la
 * ligne `visio_room_checklists` de la salle (série choisie + ids cochés, RLS
 * tuteur de la salle) ; écrit en upsert direct PostgREST. `packages/visio` ne
 * dépend pas de `@jeraisonne/db` : la forme des points est relue ici, avec la
 * même tolérance (une ligne mal formée ne casse jamais l'écran).
 */

export interface ChecklistPoint {
  id: string;
  text: string;
}

export interface ChecklistSeries {
  id: string;
  title: string;
  points: ChecklistPoint[];
}

function parsePoints(raw: unknown): ChecklistPoint[] {
  if (!Array.isArray(raw)) return [];
  const points: ChecklistPoint[] = [];
  for (const item of raw) {
    const { id, text } = (item ?? {}) as { id?: unknown; text?: unknown };
    if (typeof id === 'string' && id && typeof text === 'string' && text.trim()) {
      points.push({ id, text });
    }
  }
  return points;
}

/**
 * Série choisie à la CRÉATION du rendez-vous (`rendezvous_series_choices`, migration
 * 0248), retrouvée via la réservation de la salle. Meilleur effort : `null` au
 * moindre doute.
 */
async function fetchPlannedSeriesId(db: SupabaseClient, roomId: string): Promise<string | null> {
  const { data: room } = await db
    .from('video_rooms')
    .select('reservation_id')
    .eq('id', roomId)
    .maybeSingle();
  const reservationId = (room as { reservation_id: string | null } | null)?.reservation_id;
  if (!reservationId) return null;
  const { data } = await db
    .from('rendezvous_series_choices')
    .select('series_id')
    .eq('reservation_id', reservationId)
    .maybeSingle();
  return (data as { series_id: string | null } | null)?.series_id ?? null;
}

export interface UseSeriesChecklistResult {
  loading: boolean;
  error: string | null;
  series: ChecklistSeries[];
  /** Série choisie pour cette salle (null = aucune, ou supprimée depuis). */
  active: ChecklistSeries | null;
  checkedIds: string[];
  selectSeries: (seriesId: string | null) => void;
  toggle: (pointId: string) => void;
}

export function useSeriesChecklist(db: SupabaseClient, roomId: string): UseSeriesChecklistResult {
  const [series, setSeries] = useState<ChecklistSeries[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Dernier état confirmé côté serveur : c'est lui qu'on rétablit si une écriture échoue.
  const savedRef = useRef<{ seriesId: string | null; checked: string[] }>({
    seriesId: null,
    checked: [],
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [seriesRes, checklistRes] = await Promise.all([
        db.from('visio_instruction_series').select('id, title, points').order('created_at'),
        db
          .from('visio_room_checklists')
          .select('series_id, checked_ids')
          .eq('room_id', roomId)
          .maybeSingle(),
      ]);
      if (cancelled) return;
      if (seriesRes.error) {
        setError('Impossible de charger tes séries de points.');
        setLoading(false);
        return;
      }
      const loaded = ((seriesRes.data ?? []) as { id: string; title: string; points: unknown }[])
        .map((row) => ({ id: row.id, title: row.title, points: parsePoints(row.points) }))
        .filter((s) => s.points.length > 0);
      setSeries(loaded);

      const row = checklistRes.data as { series_id: string | null; checked_ids: string[] } | null;
      if (row && row.series_id && loaded.some((s) => s.id === row.series_id)) {
        setActiveId(row.series_id);
        setCheckedIds(row.checked_ids ?? []);
        savedRef.current = { seriesId: row.series_id, checked: row.checked_ids ?? [] };
      } else {
        // Rien de choisi PENDANT la visio : on propose la série prévue à la création du
        // rendez-vous (non enregistrée tant que le tuteur ne coche rien).
        const planned = await fetchPlannedSeriesId(db, roomId);
        if (cancelled) return;
        if (planned && loaded.some((s) => s.id === planned)) setActiveId(planned);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [db, roomId]);

  const persist = useCallback(
    async (seriesId: string | null, checked: string[]) => {
      const { error: writeError } = await db
        .from('visio_room_checklists')
        .upsert(
          { room_id: roomId, series_id: seriesId, checked_ids: checked },
          { onConflict: 'room_id' },
        );
      if (writeError) throw writeError;
      savedRef.current = { seriesId, checked };
    },
    [db, roomId],
  );

  const selectSeries = useCallback(
    (seriesId: string | null) => {
      setError(null);
      setActiveId(seriesId);
      setCheckedIds([]); // les points cochés appartenaient à l'ancienne série
      persist(seriesId, []).catch((err: unknown) => {
        console.error('[series] choix de la série non enregistré', err);
        setError('Le choix de la série n’a pas pu être enregistré.');
        setActiveId(savedRef.current.seriesId);
        setCheckedIds(savedRef.current.checked);
      });
    },
    [persist],
  );

  const toggle = useCallback(
    (pointId: string) => {
      if (!activeId) return;
      setError(null);
      const next = checkedIds.includes(pointId)
        ? checkedIds.filter((id) => id !== pointId)
        : [...checkedIds, pointId];
      setCheckedIds(next); // optimiste
      persist(activeId, next).catch((err: unknown) => {
        console.error('[series] point non enregistré', err);
        setError('Ce point n’a pas pu être enregistré.');
        setCheckedIds(savedRef.current.checked);
      });
    },
    [activeId, checkedIds, persist],
  );

  return {
    loading,
    error,
    series,
    active: series.find((s) => s.id === activeId) ?? null,
    checkedIds,
    selectSeries,
    toggle,
  };
}
